import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { apiGet, apiPost } from '../lib/api';

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'void' | 'overdue';

interface InvoiceRow {
  id: string;
  invoice_number: string;
  status: InvoiceStatus;
  currency: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  due_date: string;
  issue_date: string;
  client_id: string;
  project_id: string;
  payment_reference: string | null;
  sent_at: string | null;
  paid_at: string | null;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface ClientOption {
  id: string;
  display_name: string;
}

interface CurrencyTotal {
  currency: string;
  total: number;
}

export function InvoicesPage() {
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [projectFilter, setProjectFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadData();
  }, [statusFilter, projectFilter, clientFilter]);

  async function loadData(): Promise<void> {
    setLoading(true);
    setError(null);

    const query = new URLSearchParams();
    if (statusFilter !== 'all') query.set('status', statusFilter);
    if (projectFilter) query.set('project_id', projectFilter);
    if (clientFilter) query.set('client_id', clientFilter);

    try {
      const [invoiceData, projectData, clientData] = await Promise.all([
        apiGet<InvoiceRow[]>(`/api/invoices${query.toString() ? `?${query.toString()}` : ''}`),
        apiGet<ProjectOption[]>('/api/projects'),
        apiGet<ClientOption[]>('/api/clients'),
      ]);
      setInvoices(invoiceData);
      setProjects(projectData);
      setClients(clientData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }

  async function sendInvoice(invoiceId: string): Promise<void> {
    setBusyInvoiceId(invoiceId);
    setError(null);
    try {
      await apiPost(`/api/invoices/${invoiceId}/send`, {}, { successMessage: 'Invoice sent' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setBusyInvoiceId(null);
    }
  }

  async function voidInvoice(invoiceId: string): Promise<void> {
    setBusyInvoiceId(invoiceId);
    setError(null);
    try {
      await apiPost(`/api/invoices/${invoiceId}/void`, {}, { successMessage: 'Invoice voided' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void invoice');
    } finally {
      setBusyInvoiceId(null);
    }
  }

  const summaryStats = useMemo(() => {
    const totalInvoiced = totalsByStatusAndCurrency(invoices, [
      'draft',
      'sent',
      'paid',
      'void',
      'overdue',
    ]);
    const outstanding = totalsByStatusAndCurrency(invoices, ['sent', 'overdue']);
    const totalPaid = totalsByStatusAndCurrency(invoices, ['paid']);
    const draftCount = invoices.filter((i) => i.status === 'draft').length;
    const overdueCount = invoices.filter((i) => i.status === 'overdue').length;
    return { totalInvoiced, outstanding, totalPaid, draftCount, overdueCount };
  }, [invoices]);

  const visible = useMemo(() => {
    const lower = keyword.trim().toLowerCase();
    if (!lower) return invoices;
    return invoices.filter((inv) => {
      const projName = projectNameOf(projects, inv.project_id).toLowerCase();
      const cliName = clientNameOf(clients, inv.client_id).toLowerCase();
      return (
        inv.invoice_number.toLowerCase().includes(lower) ||
        (inv.payment_reference ?? '').toLowerCase().includes(lower) ||
        projName.includes(lower) ||
        cliName.includes(lower)
      );
    });
  }, [invoices, keyword, projects, clients]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Invoices</h1>
          <p className="mt-1 text-sm text-slate-600">
            Invoice overview and management. Create invoices from the Project detail page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void loadData();
          }}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <StatCard label="Total Invoiced" value={formatCurrencyTotals(summaryStats.totalInvoiced)} />
        <StatCard
          label="Outstanding"
          value={formatCurrencyTotals(summaryStats.outstanding)}
          accent={summaryStats.outstanding.some((row) => row.total > 0) ? 'amber' : undefined}
        />
        <StatCard
          label="Paid"
          value={formatCurrencyTotals(summaryStats.totalPaid)}
          accent="emerald"
        />
        <StatCard label="Drafts" value={String(summaryStats.draftCount)} accent="purple" />
        <StatCard
          label="Overdue"
          value={String(summaryStats.overdueCount)}
          accent={summaryStats.overdueCount > 0 ? 'rose' : undefined}
        />
      </div>

      {countActiveCurrencies([
        ...summaryStats.totalInvoiced,
        ...summaryStats.outstanding,
        ...summaryStats.totalPaid,
      ]) > 1 ? (
        <p className="mt-2 text-xs text-slate-500">
          Invoice totals are displayed per currency (not cross-currency summed).
        </p>
      ) : null}

      <div className="mt-4 grid gap-2 md:grid-cols-4">
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="Search invoice / reference / client / project"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="void">Void</option>
        </select>
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name}
            </option>
          ))}
        </select>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {loading ? <p className="mt-3 text-sm text-slate-500">Loading invoices...</p> : null}

      <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
              <th className="px-3 py-2">Invoice</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Project</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Issue / Due</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 align-top last:border-0">
                <td className="px-3 py-2">
                  <Link
                    to={`/invoices/${inv.id}`}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {inv.invoice_number}
                  </Link>
                  {inv.payment_reference ? (
                    <p className="mt-0.5 text-xs text-slate-500">Ref: {inv.payment_reference}</p>
                  ) : null}
                </td>
                <td className="px-3 py-2">{clientNameOf(clients, inv.client_id)}</td>
                <td className="px-3 py-2">
                  <Link
                    to={`/projects/${inv.project_id}`}
                    className="text-brand-700 hover:underline"
                  >
                    {projectNameOf(projects, inv.project_id)}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={inv.status} />
                </td>
                <td className="px-3 py-2 text-right font-medium">
                  {formatMoney(inv.total, inv.currency)}
                </td>
                <td className="px-3 py-2">
                  <p>
                    {shortDate(inv.issue_date)} &rarr; {shortDate(inv.due_date)}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {inv.paid_at
                      ? `Paid ${shortDate(inv.paid_at)}`
                      : inv.sent_at
                        ? `Sent ${shortDate(inv.sent_at)}`
                        : 'Not sent'}
                  </p>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {inv.status === 'draft' ? (
                      <button
                        type="button"
                        onClick={() => {
                          void sendInvoice(inv.id);
                        }}
                        disabled={busyInvoiceId === inv.id}
                        className="rounded bg-brand-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                      >
                        {busyInvoiceId === inv.id ? 'Sending...' : 'Send'}
                      </button>
                    ) : null}
                    {inv.status === 'draft' || inv.status === 'sent' || inv.status === 'overdue' ? (
                      <button
                        type="button"
                        onClick={() => {
                          void voidInvoice(inv.id);
                        }}
                        disabled={busyInvoiceId === inv.id}
                        className="rounded border border-rose-300 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        {busyInvoiceId === inv.id ? 'Updating...' : 'Void'}
                      </button>
                    ) : null}
                    {inv.status === 'paid' ? (
                      <span className="rounded border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                        Paid
                      </span>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No invoices found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard(props: { label: string; value: string; accent?: string }) {
  const colorMap: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    purple: 'border-purple-200 bg-purple-50',
  };
  const cls = colorMap[props.accent ?? ''] ?? 'border-slate-200 bg-slate-50';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <p className="text-xs text-slate-500">{props.label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{props.value}</p>
    </div>
  );
}

function StatusBadge(props: { status: InvoiceStatus }) {
  const styles: Record<InvoiceStatus, string> = {
    paid: 'bg-emerald-50 text-emerald-700',
    sent: 'bg-blue-50 text-blue-700',
    overdue: 'bg-amber-50 text-amber-700',
    void: 'bg-slate-100 text-slate-600',
    draft: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[props.status]}`}>
      {props.status}
    </span>
  );
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

function formatMoneyWithCode(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }).format(value);
}

function totalsByStatusAndCurrency(
  invoices: InvoiceRow[],
  statuses: InvoiceStatus[],
): CurrencyTotal[] {
  const byCurrency = new Map<string, number>();

  for (const invoice of invoices) {
    if (!statuses.includes(invoice.status)) {
      continue;
    }
    const currency = invoice.currency.toUpperCase();
    byCurrency.set(currency, (byCurrency.get(currency) ?? 0) + invoice.total);
  }

  return Array.from(byCurrency.entries())
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

function formatCurrencyTotals(totals: CurrencyTotal[]): string {
  if (totals.length === 0) {
    return '$0.00';
  }

  if (totals.length === 1) {
    const first = totals[0];
    if (!first) {
      return '$0.00';
    }
    return formatMoney(first.total, first.currency);
  }

  return totals.map((row) => formatMoneyWithCode(row.total, row.currency)).join(' · ');
}

function countActiveCurrencies(totals: CurrencyTotal[]): number {
  return new Set(totals.filter((row) => row.total > 0).map((row) => row.currency)).size;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

function projectNameOf(projects: ProjectOption[], id: string): string {
  return projects.find((p) => p.id === id)?.name ?? id;
}

function clientNameOf(clients: ClientOption[], id: string): string {
  return clients.find((c) => c.id === id)?.display_name ?? id;
}
